import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, CardContent, Typography, TextField, Button, CircularProgress, Snackbar, Alert, Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import IE_Logo from '../assets/IE_Logo.png';
import TPC_Logo from '../assets/TPC_Logo.png';

const loginMorphA = keyframes`
  0% {
    transform: translate3d(-1.2%, -1%, 0) scale(1.01);
    opacity: 0.36;
  }
  50% {
    transform: translate3d(1.1%, 0.8%, 0) scale(1.045);
    opacity: 0.46;
  }
  100% {
    transform: translate3d(-0.7%, 1.1%, 0) scale(1.02);
    opacity: 0.4;
  }
`;

const loginMorphB = keyframes`
  0% {
    transform: translate3d(1.1%, -0.8%, 0) scale(1.04);
    opacity: 0.22;
  }
  50% {
    transform: translate3d(-1%, 1.1%, 0) scale(1.01);
    opacity: 0.3;
  }
  100% {
    transform: translate3d(0.9%, -0.7%, 0) scale(1.03);
    opacity: 0.24;
  }
`;

function Login({ setUser, supabase }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setUser(data.user);
      navigate('/'); // Redirect to dashboard
    } catch (err) {
      console.error('Login error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        background:
          'radial-gradient(1200px 640px at 10% -10%, rgba(31, 92, 74, 0.42), transparent 58%), radial-gradient(960px 560px at 100% 0%, rgba(227, 83, 2, 0.26), transparent 56%), linear-gradient(180deg, #eef5f2 0%, #dfeae6 100%)',
        minHeight: '100vh',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-12%',
          zIndex: -1,
          pointerEvents: 'none',
          background:
            'radial-gradient(960px 540px at 14% 18%, rgba(15, 118, 110, 0.28), transparent 60%), radial-gradient(900px 500px at 84% 14%, rgba(227, 83, 2, 0.18), transparent 58%), radial-gradient(1200px 700px at 52% 110%, rgba(46, 111, 93, 0.14), transparent 62%)',
          filter: 'blur(14px)',
          animation: `${loginMorphA} 42s ease-in-out infinite alternate`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: '-14%',
          zIndex: -1,
          pointerEvents: 'none',
          background:
            'radial-gradient(880px 520px at 74% 24%, rgba(15, 118, 110, 0.18), transparent 56%), radial-gradient(760px 460px at 28% 78%, rgba(227, 83, 2, 0.14), transparent 60%)',
          filter: 'blur(22px)',
          animation: `${loginMorphB} 55s ease-in-out infinite alternate`,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '&::before, &::after': {
            animation: 'none',
          },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(17, 47, 38, 0.12)',
            background:
              'linear-gradient(160deg, rgba(255, 255, 255, 0.96), rgba(248, 252, 250, 0.9) 45%, rgba(255, 247, 242, 0.88))',
            boxShadow: '0 24px 48px rgba(13, 37, 31, 0.16)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                position: 'relative',
                mx: { xs: -3, md: -4 },
                mt: { xs: -3, md: -4 },
                mb: 3.2,
                px: { xs: 1.25, md: 2.25 },
                py: 1.35,
                background:
                  'linear-gradient(100deg, rgba(9, 54, 51, 0.98) 0%, rgba(15, 118, 110, 0.96) 46%, rgba(39, 106, 98, 0.96) 100%)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
                boxShadow: '0 14px 30px rgba(11, 40, 35, 0.24)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  opacity: 0.35,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255, 255, 255, 0.18) 0px, rgba(255, 255, 255, 0.18) 1px, transparent 1px, transparent 22px)',
                }}
              />
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                  alignItems: 'center',
                  columnGap: 1.2,
                }}
              >
                <img src={IE_Logo} alt="Ivanhoe Electric Logo" style={{ height: '34px', width: 'auto' }} />
                <Box />
                <img src={TPC_Logo} alt="Tipple Consulting Logo" style={{ height: '34px', width: 'auto' }} />
              </Box>
            </Box>
            <Typography variant="h4" align="center" gutterBottom sx={{ color: 'var(--color-brand-dark)', fontWeight: 'bold', mb: 0.8 }}>
              Field Safety Dashboard Login
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: 'var(--color-muted)', mb: 2.2 }}>
              Sign in to monitor conditions, alerts, and contacts by site.
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{
                  mb: 0.2,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderColor: 'var(--color-brand)' },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': { color: 'var(--color-brand)' },
                  },
                }}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderColor: 'var(--color-brand)' },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': { color: 'var(--color-brand)' },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  marginTop: 3,
                  backgroundColor: 'var(--color-brand)',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'var(--color-brand-dark)' },
                  padding: '11px 0',
                  boxShadow: 'none',
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
        {error && (
          <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
            <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
      </Container>
    </Box>
  );
}

export default Login;
